import type { CdkDragDrop } from '@angular/cdk/drag-drop';
import { CdkDrag, CdkDropList, moveItemInArray } from '@angular/cdk/drag-drop';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  output,
} from '@angular/core';

import { TranslatePipe } from '@recruitee/i18n';

import { BadgeComponent } from '../../common/badge/badge.component';
import { IconButtonComponent } from '../../common/button/components/icon-button/icon-button.component';
import { DividerComponent } from '../../common/divider/divider.component';
import { DropdownHeaderComponent } from '../../common/dropdown/dropdown-header/dropdown-header.component';
import { DropdownItemComponent } from '../../common/dropdown/dropdown-item/dropdown-item.component';
import { DropdownComponent } from '../../common/dropdown/dropdown.component';
import { IconComponent } from '../../common/icon/icon.component';
import {
  ConnectedOverlayDirective,
  OverlayOriginDirective,
} from '../../core/overlay/overlay-directives';
import { CheckboxComponent } from '../../forms/checkbox/checkbox.component';
import type { DataTableColumnWithVisibility } from '../data-table.types';

@Component({
  selector: 'rt-data-table-options-button',
  styleUrls: ['./data-table-options-button.component.less'],
  templateUrl: './data-table-options-button.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    BadgeComponent,
    CdkDrag,
    CdkDropList,
    CheckboxComponent,
    ConnectedOverlayDirective,
    DividerComponent,
    DropdownComponent,
    DropdownHeaderComponent,
    DropdownItemComponent,
    IconButtonComponent,
    IconComponent,
    OverlayOriginDirective,
    TranslatePipe,
  ],
})
export class DataTableOptionsButtonComponent {
  public columns: DataTableColumnWithVisibility[];
  public hoverEffects: boolean = true;
  public dropdownOpened: boolean = false;

  @Input() public tableAutoFit: boolean;
  @Input() public tableColumns: DataTableColumnWithVisibility[];
  @Input() public hideAutoFit: boolean;
  @Input() public disabled: boolean;

  public tableAutoFitChange = output<boolean>();
  public tableColumnsChange = output<DataTableColumnWithVisibility[]>();

  public trackDropdownOpen = output<true>();
  public trackColumnVisibilityChange = output<DataTableColumnWithVisibility>();
  public trackColumnPositionChange = output<DataTableColumnWithVisibility>();

  constructor(private cdRef: ChangeDetectorRef) {}

  public toggleTableConfigDropdown(): void {
    this.dropdownOpened = !this.dropdownOpened;
    this.cdRef.detectChanges();

    if (this.dropdownOpened) this.trackDropdownOpen.emit(true);
  }

  public toggleColumnVisibility(column: DataTableColumnWithVisibility): void {
    if (column.options.removable) {
      this.tableColumnsChange.emit(
        this.tableColumns.map(c => (column.id === c.id ? { ...c, visible: !c.visible } : c)),
      );
    }

    this.trackColumnVisibilityChange.emit({ ...column, visible: !column.visible });
  }

  public trackByColumnId(index: number, column: DataTableColumnWithVisibility): string {
    return column.id;
  }

  public onDrop(event: CdkDragDrop<DataTableColumnWithVisibility>): void {
    const { previousIndex, currentIndex } = event;
    const newColumns = [...this.tableColumns];

    moveItemInArray(newColumns, previousIndex, currentIndex);
    this.tableColumnsChange.emit(newColumns);

    this.trackColumnPositionChange.emit(newColumns[currentIndex]);
  }
}
